import CardDropdown from '@/components/common/CardDropdown';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import Flex from '@/components/common/Flex';
import SubtleBadge from '@/components/common/SubtleBadge';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import classNames from 'classnames';
import { Button, Card, Dropdown, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Avatar, { AvatarGroup } from '@/components/common/Avatar';
import {
  breakpointsMediaQuery,
  getColor,
  routeToHandler,
  useMediaQuery,
} from '@/helpers/utils';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toolRoutes } from '@/routes/siteMaps';
import { useDispatch } from 'react-redux';
import { setDomain } from '@/app/common/reducers/domainSlice';
import { warningMessage } from '@/utils/toast';
import countSuffix from '@/helpers/countSuffix';
import DomainFavicon from '@/app/common/components/DomainFavicon';
import type { Dispatch, SetStateAction } from 'react';
import type { DomainCardData, DomainDeleteTrigger } from './types';

export const accessLevelRenderer = (level: number) => {
  switch (level) {
    case 1:
      return { label: 'مالک', color: 'success' };
    case 2:
      return { label: 'مدیر', color: 'info' };
    case 3:
      return { label: 'دیدن', color: 'warning' };
    default:
      return { label: 'ناشناخته', color: 'danger' };
  }
};

interface DomainCardProps {
  data: DomainCardData;
  triggeredRem: DomainDeleteTrigger;
  setTriggeredRem: Dispatch<SetStateAction<DomainDeleteTrigger>>;
}

const DomainCard = ({ data, triggeredRem: _triggeredRem, setTriggeredRem }: DomainCardProps) => {
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { url, show_name, access_level, slug, num_of_keywords } = data;

  const openAccessManagement = () => {
    if (access_level === 1) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.append('domain_slug', slug);
      newSearchParams.append('url', url);
      setSearchParams(newSearchParams);
    } else {
      warningMessage(
        `شما مالک این دامنه نیستید، فقط مالک دامنه (${data?.owner}) میتواند به این بخش دسترسی داشته باشد.`,
      );
    }
  };

  return (
    <Row className="g-3 mx-0 mb-3">
      <Card className="p-0">
        <style>{`#dropdown-domain-tools::after{
          display: none;
          }`}</style>
        <FalconCardHeader
          light
          title={
            <Flex className="gap-1" alignItems="start">
              <h5 className="mb-0 fw-bolder text-truncate fs-0">
                <DomainFavicon domain={url} /> {url}
              </h5>

              <SubtleBadge
                pill
                bg={accessLevelRenderer(access_level).color}
                className="fs--2"
              >
                {accessLevelRenderer(access_level).label}
              </SubtleBadge>
            </Flex>
          }
          titleTag="h5"
          className="py-3"
          endEl={
            <Flex className="gap-2">
              <CardDropdown>
                <Dropdown.Item
                  onClick={openAccessManagement}
                  className={classNames(
                    'link-600 fs--1 d-flex align-items-center gap-1 p-2',
                  )}
                >
                  <FontAwesomeIcon icon="user-cog" className="me-1" />
                  مدیریت دسترسی
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => {
                    if (access_level === 1) {
                      setTriggeredRem({
                        mode: 'delete_domain',
                        url,
                        slug,
                      });
                    } else {
                      setTriggeredRem({
                        mode: 'delete_access',
                        url,
                        slug,
                      });
                    }
                  }}
                  className="text-danger fs--1 d-flex align-items-center gap-1 p-2"
                >
                  {access_level === 1 ? (
                    <FontAwesomeIcon icon="trash-alt" className="mx-1" />
                  ) : (
                    <FontAwesomeIcon icon="user-xmark" className="me-1" />
                  )}
                  {access_level === 1 ? 'حذف' : 'حذف دسترسی'}
                </Dropdown.Item>
              </CardDropdown>
            </Flex>
          }
        >
          <div
            className="fs--2 fw-medium text-400 "
            style={{ marginRight: 20 }}
          >
            {show_name}
          </div>
        </FalconCardHeader>
        <Card.Footer className="border-top flex-wrap px-3 align-items-center justify-content-between d-flex bg-body-tertiary p-1">
          <div className="cursor-pointer" onClick={openAccessManagement}>
            <AvatarGroup dense className="mt-2 mb-2">
              {data?.members?.map(({ image, name, family }, index) => (
                <Avatar
                  src={image && image}
                  key={index}
                  name={name || family ? `${name} ${family}` : ''}
                  tooltip={name || family ? `${name} ${family}` : ''}
                  isExact
                  size="xl"
                  className="border border-3 rounded-circle border-200"
                />
              ))}
              <Avatar
                tooltip={access_level === 1 ? '' : `مالک: ${data?.owner}`}
                name={
                  <FontAwesomeIcon
                    icon={access_level === 1 ? 'user-cog' : 'lock'}
                    size="lg"
                    className={`${access_level === 1 ? 'me-1' : ''} text-primary`}
                  />
                }
                mediaClass="position-static"
                isExact
                size="xl"
                className="border border-3 rounded-circle border-200 "
              />
            </AvatarGroup>
          </div>
          <Flex
            className="gap-2 my-3"
            wrap="wrap"
            direction="row"
            alignItems="center"
          >
            {toolRoutes.children.map(tool => (
              <ChildrensTooltip
                delayToRemove={0}
                placement="top"
                key={tool.name}
                html={tool.name}
              >
                {tool.children?.length > 0 ? (
                  <Dropdown>
                    <Dropdown.Toggle
                      style={{
                        color: getColor(
                          'navbar-vertical-default-link-active-color',
                        ),
                      }}
                      className="position-relative"
                      as={isMdUp ? Button : 'div'}
                      variant="falcon-info"
                      size="sm"
                      id="dropdown-domain-tools"
                    >
                      {tool.name === 'ردیاب کلمات' && num_of_keywords !== 0 && (
                        <div
                          style={{ right: -13, top: -7, fontSize: 12 }}
                          className="position-absolute"
                        >
                          <SubtleBadge
                            bg="primary"
                            className="d-flex align-items-center fw-bolder gap-1 "
                          >
                            {countSuffix(num_of_keywords)?.toPersianDigit()}
                          </SubtleBadge>
                        </div>
                      )}
                      {typeof tool.icon === 'string' ? (
                        <FontAwesomeIcon icon={tool.icon} />
                      ) : (
                        tool.icon
                      )}
                    </Dropdown.Toggle>
                    {tool.children?.length > 0 && (
                      <Dropdown.Menu className="p-0">
                        {tool.children.map(child => (
                          <Dropdown.Item
                            key={child.name}
                            className={classNames('d-flex align-items-center', {
                              'disabled-link': child.disabled,
                            })}
                            style={{ textDecoration: 'none' }}
                            onClick={() => {
                              navigate(
                                routeToHandler(
                                  child,
                                  { name: url, slug },
                                  url,
                                  slug,
                                ),
                              );
                              setTimeout(() => {
                                dispatch(setDomain({ name: url, slug }));
                              }, 20);
                            }}
                          >
                            {child.name}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    )}
                  </Dropdown>
                ) : (
                  <Dropdown.Toggle
                    as={isMdUp ? Button : 'div'}
                    className="position-relative"
                    disabled={tool.disabled}
                    style={{
                      color: getColor(
                        'navbar-vertical-default-link-active-color',
                      ),
                    }}
                    onClick={() => {
                      navigate(
                        routeToHandler(tool, { name: url, slug }, url, slug),
                      );
                      setTimeout(() => {
                        dispatch(setDomain({ name: url, slug }));
                      }, 20);
                    }}
                    id="dropdown-domain-tools"
                    variant="falcon-info"
                    size="sm"
                  >
                    {typeof tool.icon === 'string' ? (
                      <FontAwesomeIcon icon={tool.icon} />
                    ) : (
                      tool.icon
                    )}
                  </Dropdown.Toggle>
                )}
              </ChildrensTooltip>
            ))}
          </Flex>
        </Card.Footer>
      </Card>
    </Row>
  );
};

export default DomainCard;
